const isPlainObject = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const STATE_KEY_ALIASES = [
    ['displayLabel', 'label'],
    ['displayValue', 'asValue'],
    ['value', 'asValue'],
    ['displayAsText', 'asText'],
    ['capitalize', 'textCase'],
    ['prefix', 'textPrefix'],
    ['suffix', 'textSuffix'],
    ['url', 'linkUrl'],
    ['urlStorage', 'linkUrlStorage'],
    ['disableNewLinkIcon', 'linkDisableTabIcon'],
    ['showTime', 'dateIncludeTime'],
    ['showAsDays', 'dateRelativeUnit'],
    ['displaySize', 'size'],
    ['justifyContent', 'contentAlignment'],
    ['_styles', 'styles'],
    ['tooltipOptions', '_tooltip'],
    ['useFallbackTooltip', '_tooltip'],
    ['tableState', '_tableColumns'],
    ['props', 'widgetProps'],
    ['filterOnLocator', 'filterLocator'],
    ['sortOnLocator', 'sortLocator'],
    ['distinctFilterInputType', 'filterDistinctInputType'],
    ['distinctValueKey', 'filterDistinctValueKey'],
    ['series', '_series'],
    ['chartSeries', '_series'],
    ['reference_lines', '_referenceLines'],
    ['chartReferenceLines', '_referenceLines'],
    ['_conditionalColorRules', 'conditionalColorRules'],
    ['conditional_color', 'conditionalColorRules'],
    ['top_n', 'dataTopN'],
    ['chartTopN', 'dataTopN'],
    ['sort_by', 'dataSort'],
    ['chartSortBy', 'dataSort'],
    ['categoryFilter', 'dataFilter'],
    ['null_value_behavior', 'chartNullValueBehavior'],
    ['chartTitle', 'title'],
    ['chartSubtitle', 'subtitle'],
    ['chartDescription', 'description'],
    ['x_axis_label', 'xAxisLabel'],
    ['y_axis_label', 'yAxisLabel'],
    ['value_format', 'numberUnit'],
    ['useShorthand', 'numberDisplayFormat'],
    ['precision', 'numberPrecision'],
    ['locale', 'numberLocale'],
    ['nullDisplay', 'numberNullDisplay'],
    ['roundingMode', 'numberRoundingMode'],
    ['goalDirection', 'goalCondition'],
    ['dimension_locator', 'dimensionLocator'],
    ['color_palette', 'colorPalette'],
    ['line_width', 'lineWidth'],
    ['line_style', 'lineStyle'],
    ['curve_type', 'curveType'],
    ['show_data_points', 'showDataPoints'],
    ['point_radius', 'pointRadius'],
    ['fill_area', 'fillArea'],
    ['fill_opacity', 'fillOpacity'],
    ['bar_radius', 'barRadius'],
    ['bar_padding', 'barPadding'],
    ['tooltip_enabled', 'tooltipEnabled'],
    ['chartTooltipEnabled', 'tooltipEnabled'],
    ['show_legend', 'showLegend'],
    ['chartShowLegend', 'showLegend'],
    ['legend_position', 'legendPosition'],
    ['legend_orientation', 'legendOrientation'],
    ['show_x_axis_ticks', 'showXAxisTicks'],
    ['chartShowXAxisTicks', 'showXAxisTicks'],
    ['show_y_axis_ticks', 'showYAxisTicks'],
    ['chartShowYAxisTicks', 'showYAxisTicks'],
    ['show_x_gridlines', 'showXGridlines'],
    ['chartShowXGridlines', 'showXGridlines'],
    ['show_y_gridlines', 'showYGridlines'],
    ['chartShowYGridlines', 'showYGridlines'],
    ['y_ticks', 'yTicks'],
    ['chartYTicks', 'yTicks'],
    ['tick_interval', 'tickInterval'],
    ['tick_interval_custom', 'tickIntervalCustom'],
    ['y_axis_min', 'yAxisMin'],
    ['chartYAxisMin', 'yAxisMin'],
    ['y_axis_max', 'yAxisMax'],
    ['chartYAxisMax', 'yAxisMax'],
    ['y_axis_tick_count', 'yAxisTickCount'],
    ['chartYAxisTickCount', 'yAxisTickCount'],
    ['show_data_labels', 'showDataLabels'],
    ['chartShowDataLabels', 'showDataLabels'],
    ['data_label_position', 'dataLabelPosition'],
    ['chartOrientation', 'orientation'],
    ['color_by_dimension', 'colorByDimension'],
    ['chartColorByDimension', 'colorByDimension'],
    ['chartHeight', 'height'],
    ['value_font_size', 'size'],
    ['show_comparison', 'showComparison'],
    ['comparison_direction', 'comparisonDirection'],
    ['comparison_format', 'comparisonFormat'],
    ['show_sparkline', 'showSparkline'],
    ['sparkline_type', 'sparklineType'],
    ['expandAs', 'expansionMode'],
];
const LEGACY_TO_CANONICAL = new Map(STATE_KEY_ALIASES);
const setIfAbsent = (target, key, value) => {
    if (!(key in target)) {
        target[key] = value;
    }
};
const normalizeShowAsDays = (value) => {
    if (value === true)
        return 'days';
    if (value === false)
        return undefined;
    return value;
};
const normalizeUseShorthand = (value) => {
    if (value === true)
        return 'abbreviated';
    if (value === false)
        return 'number';
    return value;
};
const normalizeFormatWrapper = (format, target) => {
    if (!isPlainObject(format)) {
        if (typeof format === 'string') {
            setIfAbsent(target, 'numberUnit', format);
        }
        return;
    }
    if ('style' in format)
        setIfAbsent(target, 'numberUnit', format.style);
    if ('useShorthand' in format) {
        setIfAbsent(target, 'numberDisplayFormat', normalizeUseShorthand(format.useShorthand));
    }
    if ('precision' in format)
        setIfAbsent(target, 'numberPrecision', format.precision);
    if ('locale' in format)
        setIfAbsent(target, 'numberLocale', format.locale);
    if ('nullDisplay' in format)
        setIfAbsent(target, 'numberNullDisplay', format.nullDisplay);
    if ('roundingMode' in format)
        setIfAbsent(target, 'numberRoundingMode', format.roundingMode);
};
const normalizeDateOptions = (dateOptions, target) => {
    if (!isPlainObject(dateOptions))
        return;
    if ('showTime' in dateOptions) {
        setIfAbsent(target, 'dateIncludeTime', dateOptions.showTime);
    }
};
const normalizeListOptions = (listOptions, target) => {
    if (!isPlainObject(listOptions))
        return;
    if ('preset' in listOptions) {
        setIfAbsent(target, 'listLoadingPreset', listOptions.preset);
    }
    if ('listLoadingCount' in listOptions) {
        setIfAbsent(target, 'listLoadingCount', listOptions.listLoadingCount);
    }
};
const promoteLegacyChartContainer = (state) => {
    const chart = state.chart;
    if (!isPlainObject(chart))
        return;
    const normalizedChart = normalizeStateObject(chart);
    for (const [key, value] of Object.entries(normalizedChart)) {
        setIfAbsent(state, key, value);
    }
    delete state.chart;
};
export const normalizeStateObject = (input) => {
    const result = {};
    const legacyEntries = [];
    for (const [key, value] of Object.entries(input)) {
        if (LEGACY_TO_CANONICAL.has(key)) {
            legacyEntries.push([key, value]);
            continue;
        }
        if (key === 'enableFilter' || key === 'format' || key === 'dateOptions') {
            legacyEntries.push([key, value]);
            continue;
        }
        if (key === 'listOptions') {
            legacyEntries.push([key, value]);
            continue;
        }
        result[key] = value;
    }
    for (const [legacyKey, legacyValue] of legacyEntries) {
        if (legacyKey === 'enableFilter') {
            if (legacyValue === false) {
                setIfAbsent(result, 'filterDisabled', true);
            }
            continue;
        }
        if (legacyKey === 'format') {
            normalizeFormatWrapper(legacyValue, result);
            continue;
        }
        if (legacyKey === 'dateOptions') {
            normalizeDateOptions(legacyValue, result);
            continue;
        }
        if (legacyKey === 'listOptions') {
            normalizeListOptions(legacyValue, result);
            continue;
        }
        if (legacyKey === 'useFallbackTooltip') {
            const existing = isPlainObject(result._tooltip)
                ? result._tooltip
                : undefined;
            const next = {
                ...(existing ?? {}),
                useFallback: legacyValue,
            };
            result._tooltip = next;
            continue;
        }
        const canonical = LEGACY_TO_CANONICAL.get(legacyKey);
        if (!canonical)
            continue;
        let nextValue = legacyValue;
        if (legacyKey === 'showAsDays') {
            nextValue = normalizeShowAsDays(legacyValue);
            if (nextValue === undefined)
                continue;
        }
        if (legacyKey === 'useShorthand') {
            nextValue = normalizeUseShorthand(legacyValue);
        }
        setIfAbsent(result, canonical, nextValue);
    }
    promoteLegacyChartContainer(result);
    for (const [key, value] of Object.entries(result)) {
        if (key === '_tooltip') {
            if (isPlainObject(value)) {
                result[key] = normalizeStateObject(value);
            }
            continue;
        }
        if (key === '_series' || key === '_referenceLines') {
            if (Array.isArray(value)) {
                result[key] = value.map(item => isPlainObject(item) ? normalizeStateObject(item) : item);
            }
            continue;
        }
        if (key === '_tableColumns') {
            result[key] = normalizeTableColumns(value);
            continue;
        }
    }
    return result;
};
const normalizeTableColumns = (tableColumns) => {
    if (!isPlainObject(tableColumns))
        return tableColumns;
    const next = { ...tableColumns };
    if (Array.isArray(next.columns)) {
        next.columns = next.columns.map(column => {
            if (!isPlainObject(column))
                return column;
            const col = { ...column };
            if (isPlainObject(col.state)) {
                col.state = normalizeStateObject(col.state);
            }
            return col;
        });
    }
    return next;
};
export const normalizePersistedStateTree = (input) => {
    if (Array.isArray(input)) {
        return input.map(normalizePersistedStateTree);
    }
    if (!isPlainObject(input)) {
        return input;
    }
    const result = {};
    for (const [key, value] of Object.entries(input)) {
        if (key === 'state' && isPlainObject(value)) {
            result[key] = normalizeStateObject(normalizePersistedStateTree(value));
            continue;
        }
        if (key === 'tableState' && !('_tableColumns' in input)) {
            result._tableColumns = normalizeTableColumns(normalizePersistedStateTree(value));
            continue;
        }
        if (key === 'viewState' ||
            key === 'tableConfig' ||
            key === 'widgetStates') {
            result[key] = normalizePersistedStateTree(value);
            continue;
        }
        result[key] = normalizePersistedStateTree(value);
    }
    return result;
};
export const normalizeSavedViewPayload = (savedView) => {
    return normalizePersistedStateTree(savedView);
};
export const normalizeUrlStorageValue = (value) => {
    return normalizePersistedStateTree(value);
};
export const normalizeExportInput = (input) => {
    return normalizePersistedStateTree(input);
};
export const StateAliases = {
    normalizeStateObject,
    normalizePersistedStateTree,
    normalizeSavedViewPayload,
    normalizeUrlStorageValue,
    normalizeExportInput,
    LEGACY_TO_CANONICAL,
};
//# sourceMappingURL=StateAliases.js.map